import { useMemo, useState } from 'react';

import { PagePagination } from '@/components/pagination';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/data-table';

import { useAuditLogsQuery } from '@/query/dashboard.query';

import { DEFAULT_PAGE_SIZE } from '@/constants';

import { AuditLogDetailsSheet } from './components/audit-log-details-sheet';
import { AuditLogHeader } from './components/audit-log-header';
import { getColumns } from './components/columns';
import { AuditLogEmptyResult, AuditLogErrorResult } from './components/empty';

import { useAuditLogsListStore } from '@/store/audit-logs-list.store';
import type { AuditLog } from '@/types/audit-log.type';
import { type ColumnDef } from '@tanstack/react-table';

export default function AuditLogs() {
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  const { currentPage, pageSize, filters, setCurrentPage, setPageSize, sort, query, setSort } =
    useAuditLogsListStore();

  const { data, isLoading, isError, error } = useAuditLogsQuery({
    page: currentPage,
    page_size: pageSize,
    query,
    filters,
  });

  const logs: AuditLog[] = data?.data || [];
  const meta = data?.meta;

  const columns = useMemo<ColumnDef<AuditLog>[]>(() => getColumns(), []);

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b px-4 py-4">
        <div className="space-y-1">
          <h2 className="text-sm font-semibold text-foreground">Audit trail</h2>
          <p className="text-sm text-muted-foreground">
            Review warnings, errors, and platform activity captured by the dashboard audit log.
          </p>
        </div>
        <Badge variant="secondary">{meta?.total_items || 0} events</Badge>
      </div>

      {/** Search, Filter */}
      <AuditLogHeader logs={logs} />

      <div className="overflow-hidden rounded-lg border bg-background">
        <DataTable
          columns={columns}
          data={logs}
          sorting={sort}
          isLoading={isLoading}
          onSortingChange={setSort}
          onRowClick={setSelectedLog}
          emptyState={
            isError ? (
              <AuditLogErrorResult errorMessage={error?.message} />
            ) : (
              <AuditLogEmptyResult />
            )
          }
        />

        <PagePagination
          isLoading={isLoading}
          currentPage={meta?.page || 1}
          pageSize={meta?.page_size || DEFAULT_PAGE_SIZE}
          totalItems={meta?.total_items || 0}
          totalPages={meta?.total_pages || 1}
          setPageSize={size => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          setCurrentPage={setCurrentPage}
        />
      </div>

      <AuditLogDetailsSheet log={selectedLog} onClose={() => setSelectedLog(null)} />
    </div>
  );
}
